"use client";

/**
 * The Ledger Toast and its region.
 *
 * - One region at the bottom right, one polite live region: Zag's toast group
 *   is that region; the toasts inside it are not live regions of their own.
 * - A toast echoes the action ("Key created") and leaves after 4s; it waits
 *   while the pointer is over the region or focus is in it.
 * - An error toast stays until it is closed. Its copy comes from
 *   `showErrorToast` (`~/features/errors`), never from an error's own message
 *   (the `no-raw-error-toast` rule).
 * - Every toast has a close button; call sites cannot opt out.
 *
 * The surface is the `toast` recipe (`src/theme/recipes/layers.ts`); the
 * stacking is the layer manager's toast band.
 *
 * Spec: specs/design-system/ledger-layers.feature, specs/components/toasts.feature
 */
import {
  Box,
  Toaster as ChakraToaster,
  createToaster,
  Portal,
  Stack,
  Toast,
} from "@chakra-ui/react";
import { AlertCircle, CheckCircle2, Info, TriangleAlert } from "lucide-react";

import { ErrorActions } from "~/features/errors/components/ErrorActions";
import {
  type SheetClearance,
  useOpenSheetClearance,
} from "~/hooks/useOpenSheetClearance";
import { layerZIndex } from "~/theme/layerStack";
import { Spinner } from "./spinner";

/** How long a toast stays, unless it is an error (which stays until closed). */
export const TOAST_DURATION_MS = 4000;
export const TOAST_PLACEMENT = "bottom-end" as const;

const toaster_ = createToaster({
  placement: TOAST_PLACEMENT,
  pauseOnPageIdle: true,
});

/**
 * `closable` is not an option: the Toaster renders the close button on every
 * toast unconditionally, so no call site can create a toast the user cannot
 * dismiss.
 */
type ToastCreateArgs = Omit<Parameters<typeof toaster_.create>[0], "meta"> & {
  meta?: Record<string, unknown> & { closable?: never };
};

// Workaround for https://github.com/chakra-ui/chakra-ui/issues/9490#issuecomment-2601014577
export const toaster = {
  ...toaster_,
  create: (args: ToastCreateArgs) => {
    return toaster_.create({
      duration: TOAST_DURATION_MS,
      ...args,
      // An error stays until it is read and closed, whatever the caller asked.
      ...(args.type === "error" ? { duration: Number.POSITIVE_INFINITY } : {}),
      meta: {
        ...args.meta,
        placement: TOAST_PLACEMENT,
      },
    });
  },
};

/** The status, said once, in a small glyph in its own colour. */
const STATUS_COLOR = {
  error: "var(--color-err)",
  warning: "var(--color-warn)",
  success: "var(--color-ok)",
  info: "var(--color-info)",
  loading: "var(--color-fg-3)",
} as const;

type ToastStatus = keyof typeof STATUS_COLOR;

/**
 * A toast is created with no type at all, or with one this shell does not
 * paint. Both read as information.
 */
const statusOf = (type: string | undefined): ToastStatus =>
  type && type in STATUS_COLOR ? (type as ToastStatus) : "info";

/** The height of the title's line, which the icon and close button centre on. */
const TITLE_LINE = "var(--space-5)";

function StatusIcon({ status }: { status: ToastStatus }) {
  return (
    <Box
      color={STATUS_COLOR[status]}
      display="flex"
      alignItems="center"
      height={TITLE_LINE}
      flexShrink={0}
      css={{ "& svg": { width: "var(--icon-sm)", height: "var(--icon-sm)" } }}
    >
      {status === "loading" ? (
        <Spinner size="xs" color="inherit" />
      ) : status === "success" ? (
        <CheckCircle2 aria-hidden="true" />
      ) : status === "error" ? (
        <AlertCircle aria-hidden="true" />
      ) : status === "warning" ? (
        <TriangleAlert aria-hidden="true" />
      ) : (
        <Info aria-hidden="true" />
      )}
    </Box>
  );
}

/**
 * Keeps the region off an open sheet's actions (L-10): beside the sheet, a
 * page gutter from it and from the window's edge, or above its footer.
 */
function sheetClearanceCss(clearance: SheetClearance | null) {
  if (!clearance) return {};
  if (clearance.kind === "above") {
    return {
      bottom: `calc(${clearance.bottom}px + var(--layout-page-pad)) !important`,
    };
  }
  return {
    insetInlineEnd: `calc(${clearance.inlineEnd}px + var(--layout-page-pad)) !important`,
    "& [data-part=root]": {
      maxWidth: `calc(${clearance.room}px - var(--layout-page-pad) * 2)`,
    },
  };
}

/**
 * Every toast carries a close button, a status icon, and whatever the caller
 * gave it: title, description, the docs link and error id of a handled error,
 * and one action.
 */
export const Toaster = () => {
  // With a sheet open on the right, the region moves to the sheet's left edge
  // so it never covers the sheet's footer actions (L-10).
  const sheetClearance = useOpenSheetClearance();
  return (
    <Portal>
      <ChakraToaster
        toaster={toaster}
        insetInline={{ mdDown: "var(--space-4)" }}
        // The region is the one live region; the layer manager places it
        // above every other layer.
        css={{
          zIndex: `${layerZIndex({ kind: "toast", depth: 0 })} !important`,
          ...sheetClearanceCss(sheetClearance),
        }}
        data-sheet-clearance={sheetClearance?.kind}
        data-layer="toast"
      >
        {(toast) => {
          const status = statusOf(toast.type);

          return (
            <Toast.Root
              width={{ md: "calc(var(--space-8) * 9)" }}
              // Zag makes each toast a `role="status"` live region inside the
              // region that already announces it; a group keeps one region.
              role="group"
              aria-live="off"
              data-status={status}
            >
              <StatusIcon status={status} />

              <Stack gap="var(--space-1)" flex="1" maxWidth="100%">
                {toast.title && <Toast.Title>{toast.title}</Toast.Title>}
                {toast.description && (
                  <Toast.Description>{toast.description}</Toast.Description>
                )}
                {/* Set by `showErrorToast` — the docs link and copyable error id
                    that every handled error offers. Plain `toaster.create` calls
                    leave these unset and render nothing here. */}
                <ErrorActions
                  color="var(--color-fg-3)"
                  accentColor="var(--color-link)"
                  docsUrl={
                    typeof toast.meta?.docsUrl === "string"
                      ? toast.meta.docsUrl
                      : undefined
                  }
                  traceId={
                    typeof toast.meta?.traceId === "string"
                      ? toast.meta.traceId
                      : undefined
                  }
                />
                {toast.action && (
                  <Toast.ActionTrigger
                    marginTop="var(--space-1)"
                    marginInlineStart="calc(var(--space-2) * -1)"
                    alignSelf="flex-start"
                  >
                    {toast.action.label}
                  </Toast.ActionTrigger>
                )}
              </Stack>

              {/* In the row rather than absolutely placed, sized to the title's
                  line so it never makes a one-line toast taller. */}
              <Toast.CloseTrigger
                position="static"
                alignSelf="flex-start"
                boxSize={TITLE_LINE}
                padding={0}
                flexShrink={0}
              />
            </Toast.Root>
          );
        }}
      </ChakraToaster>
    </Portal>
  );
};
